import React, { createContext, useCallback, useContext, useState } from "react"

export interface ContextData {
  contextString: string // Pre-formatted context for LLM
  data?: Record<string, unknown> // Optional data to be logged in metadata
}

interface EmbeddedChatContextType {
  // Visibility
  isOpen: boolean
  open: () => void
  close: () => void
  toggle: () => void

  // Disable (for pages with existing chat like AskPage)
  isDisabled: boolean
  disable: () => void
  enable: () => void

  // Context registration (namespace-based, merged)
  registerContext: (key: string, data: ContextData) => void
  unregisterContext: (key: string) => void
  contexts: Record<string, ContextData>

  // Path-based conversation state
  currentPathname: string
  pathConversations: Record<string, string | null>
  getConversationForPath: (pathname: string) => string | null
  setConversationForPath: (pathname: string, conversationId: string | null) => void
  handlePathChange: (newPathname: string) => void
}

const EmbeddedChatContext = createContext<EmbeddedChatContextType | undefined>(undefined)

export function EmbeddedChatProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [isDisabled, setIsDisabled] = useState(false)
  const [contexts, setContexts] = useState<Record<string, ContextData>>({})
  const [pathConversations, setPathConversations] = useState<Record<string, string | null>>({})
  const [currentPathname, setCurrentPathname] = useState<string>("")

  const open = useCallback(() => {
    if (!isDisabled) {
      setIsOpen(true)
    }
  }, [isDisabled])

  const close = useCallback(() => {
    setIsOpen(false)
  }, [])

  const toggle = useCallback(() => {
    if (!isDisabled) {
      setIsOpen((prev: boolean) => !prev)
    }
  }, [isDisabled])

  const disable = useCallback(() => {
    setIsDisabled(true)
    setIsOpen(false)
  }, [])

  const enable = useCallback(() => {
    setIsDisabled(false)
  }, [])

  const registerContext = useCallback((key: string, data: ContextData) => {
    setContexts((prev) => ({
      ...prev,
      [key]: data,
    }))
  }, [])

  const unregisterContext = useCallback((key: string) => {
    setContexts((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const getConversationForPath = useCallback((pathname: string) => {
    return pathConversations[pathname] ?? null
  }, [pathConversations])

  const setConversationForPath = useCallback((pathname: string, conversationId: string | null) => {
    setPathConversations(prev => ({ ...prev, [pathname]: conversationId }))
  }, [])

  const handlePathChange = useCallback((newPathname: string) => {
    if (newPathname !== currentPathname) {
      setCurrentPathname(newPathname)
      setIsOpen(false) // Auto-close on navigation
    }
  }, [currentPathname])

  return (
    <EmbeddedChatContext.Provider
      value={{
        isOpen,
        open,
        close,
        toggle,
        isDisabled,
        disable,
        enable,
        registerContext,
        unregisterContext,
        contexts,
        currentPathname,
        pathConversations,
        getConversationForPath,
        setConversationForPath,
        handlePathChange,
      }}
    >
      {children}
    </EmbeddedChatContext.Provider>
  )
}

export function useEmbeddedChat() {
  const context = useContext(EmbeddedChatContext)
  if (context === undefined) {
    throw new Error("useEmbeddedChat must be used within an EmbeddedChatProvider")
  }
  return context
}
